import { useState, type RefObject } from 'react';
import type { SearchResult } from '../lib/types';
import {
  extractSourcePath,
  markdownToPlainText,
  sanitizeFilename,
} from '../lib/formatters';

interface CopyButtonsProps {
  result: SearchResult;
  htmlRef: RefObject<HTMLDivElement | null>;
}

type ActionKey = 'plain' | 'email' | 'download';
type Status = 'idle' | 'done' | 'error';

const RESET_MS = 1500;

export function CopyButtons({ result, htmlRef }: CopyButtonsProps) {
  const [status, setStatus] = useState<Record<ActionKey, Status>>({
    plain: 'idle',
    email: 'idle',
    download: 'idle',
  });

  const flash = (key: ActionKey, next: Status) => {
    setStatus((s) => ({ ...s, [key]: next }));
    window.setTimeout(() => {
      setStatus((s) => ({ ...s, [key]: 'idle' }));
    }, RESET_MS);
  };

  const copyPlain = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(markdownToPlainText(result.body_md ?? ''));
      flash('plain', 'done');
    } catch {
      flash('plain', 'error');
    }
  };

  const copyEmail = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const html = htmlRef.current?.innerHTML ?? '';
    const plain = markdownToPlainText(result.body_md ?? '');
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
        const item = new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(plain);
      }
      flash('email', 'done');
    } catch {
      flash('email', 'error');
    }
  };

  const download = (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = new Blob([result.body_md ?? ''], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(result.slug)}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      flash('download', 'done');
    } catch {
      flash('download', 'error');
    }
  };

  const rawSource = result.source_path ?? extractSourcePath(result.body_md ?? '');
  const sourceHref = safeHref(rawSource);

  return (
    <div className="card-actions">
      <button type="button" className="action-btn" onClick={copyPlain}>
        {labelFor('Copy text', status.plain, 'Copied')}
      </button>
      <button type="button" className="action-btn" onClick={copyEmail}>
        {labelFor('Copy for email', status.email, 'Copied')}
      </button>
      <button type="button" className="action-btn" onClick={download}>
        {labelFor('Download .md', status.download, 'Downloaded')}
      </button>
      {sourceHref && (
        <a
          className="action-link"
          href={sourceHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          Source ↗
        </a>
      )}
    </div>
  );
}

function labelFor(base: string, status: Status, doneLabel: string): string {
  if (status === 'done') return doneLabel;
  if (status === 'error') return 'Failed';
  return base;
}

function safeHref(raw: string | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^(javascript:|data:|vbscript:)/i.test(trimmed)) return null;
  return trimmed;
}
